/**
 * @file Dynamic layered music: five 3-second stems, always in phase lock.
 *
 * SNARE loops forever, unconditionally. SCRATCH, SYNTH, KICK and BASS2 loop
 * alongside it from the very first bar — silent by default — and are only
 * ever unmuted or muted exactly on a bar boundary, never mid-bar. That
 * "never mid-bar" rule is the whole point of the design: all five sources are
 * started at the same instant and share one measured bar length, so muting a
 * layer is nothing more than scheduling its own `GainNode` to 0 or 1 at a
 * future, sample-accurate time — the underlying `AudioBufferSourceNode` never
 * stops, so nothing can ever fall out of sync with anything else.
 *
 * What decides "which layers should be on" lives entirely outside this file,
 * in {@link ../systems/MusicCueSystem.js}, which emits
 * {@link Events.MUSIC_CUE_CHANGED} whenever the match state actually changes.
 * This class only knows how to turn that into scheduled Web Audio automation:
 *
 *   SCRATCH — the player is moving.
 *   SYNTH   — an enemy is in the player's sight.
 *   KICK    — the player is shooting.
 *   BASS2   — a kill landed in the last 8 seconds.
 *   pulse   — the kill timer is low ("TIME RUNNING OUT"): a +1-tone pitch
 *             shift, gated by a bar boundary exactly like everything else.
 *
 * Two extra rules layer on top of the plain on/off gate, both in
 * `_commitBoundary`:
 *   - A track that just turned on cannot turn off again until it has played
 *     `minActiveLoops` full loops. A fresh re-activation while still inside
 *     that window restarts it, so a player who taps a condition on and off
 *     repeatedly gets one sustained layer, not a stutter.
 *   - The pitch shift has no such minimum — it is not a track — but still
 *     only lands on a boundary in both directions.
 *
 * Runs on its own `AudioContext`, same reasoning as the sequencer this
 * replaces: an `<audio>` element cannot promise the click-free, phase-locked
 * looping this design depends on throughout.
 */

import { Events } from '../core/EventBus.js';
import { GameStates } from '../core/states/StateMachine.js';
import { PlayerControlled } from '../components/index.js';
import { clamp } from '../utils/MathUtils.js';
import { loadNumber, saveNumber } from '../utils/Persisted.js';

import snareUrl from './loop/SNARE.webm';
import scratchUrl from './loop/SCRATCH.webm';
import synthUrl from './loop/SYNTH.webm';
import kickUrl from './loop/KICK.webm';
import bass2Url from './loop/BASS2.webm';

/** Track key → asset URL, resolved by Vite at build time. */
const SOURCES = {
  snare: snareUrl,
  scratch: scratchUrl,
  synth: synthUrl,
  kick: kickUrl,
  bass2: bass2Url
};

/** Every layer but SNARE, which is unconditional and never scheduled. */
const TOGGLED_LAYERS = ['scratch', 'synth', 'kick', 'bass2'];

/** `localStorage` key the music volume slider persists under. */
const VOLUME_STORAGE_KEY = 'spin:musicVolume';

export class MusicDirector {
  /**
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   *   Read live (not captured once) so `ctx.levelIndex` reflects whichever
   *   floor is current at the moment a transition actually fires.
   */
  constructor(ctx) {
    /** @type {import('../core/Context.js').GameContext} @private */
    this._ctx = ctx;
    /** @type {import('../core/EventBus.js').EventBus} @private */
    this._events = ctx.events;
    /** @type {import('../config/GameConfig.js').GameConfig['music']} @private */
    this._config = ctx.config.music;
    /** @type {AudioContext | null} @private */
    this._context = null;
    /** @type {GainNode | null} @private */
    this._master = null;
    /**
     * Own copy of the volume rather than a live read of `config.music`: the
     * shared `CONFIG` singleton must stay untouched so two `Game` instances
     * never leak a volume change into one another (see Context.js).
     * @type {number}
     * @private
     */
    this._volume = clamp(loadNumber(VOLUME_STORAGE_KEY, this._config.volume), 0, 1);
    /** @type {Map<string, AudioBuffer>} @private */
    this._buffers = new Map();
    /** @type {Promise<void> | null} @private */
    this._loading = null;

    /**
     * The five live layers, or null while nothing is playing. `active` is
     * what this class last actually committed for that layer — the ground
     * truth `_commitBoundary` diffs the desired state against. `activatedAtBar`
     * and `pendingOff` exist only for the four toggled layers (meaningless,
     * left unset, on SNARE) and implement the minimum-active-loops rule: see
     * `_commitBoundary`.
     * @type {Map<string, { source: AudioBufferSourceNode, gain: GainNode, active: boolean,
     *                       activatedAtBar?: number, pendingOff?: boolean }> | null}
     * @private
     */
    this._layers = null;

    /** @type {number} Measured length of one bar, in seconds. @private */
    this._barDuration = this._config.barDuration;
    /** @type {number} AudioContext time of the next bar boundary. @private */
    this._nextBoundaryTime = 0;
    /**
     * Combined pitch/rate multiplier from the TIME RUNNING OUT shift. Affects
     * how long, in real device time, the *next* bar will take — see the
     * config comment on `pulseSemitones` for why not the current one.
     * @type {number}
     * @private
     */
    this._rate = 1;
    /** @type {boolean} Whether the pitch shift is currently applied. @private */
    this._pulseActive = false;
    /**
     * Latest desired pitch state from `MusicCueSystem`; only `_commitBoundary`
     * decides when that becomes audible, same as the four track layers.
     * @type {boolean}
     * @private
     */
    this._desiredPulse = false;

    /**
     * Latest desired state from `MusicCueSystem`. Updated the instant the
     * event arrives; only `_commitBoundary` decides when that becomes audible.
     * @type {{ scratch: boolean, synth: boolean, kick: boolean, bass2: boolean }}
     * @private
     */
    this._desired = { scratch: false, synth: false, kick: false, bass2: false };

    /**
     * Index of the bar currently playing, counting from 0 at `_startLayers`.
     * Rate-agnostic (unlike real device time), which is what makes the
     * minimum-active-loops rule a plain integer comparison regardless of any
     * pitch-driven tempo change. Incremented once per `_commitBoundary`.
     * @type {number}
     * @private
     */
    this._barIndex = 0;

    /**
     * Bumped whenever a run starts or ends, so an in-flight `_startLayers`
     * load that finishes after being superseded knows to do nothing.
     * @type {number}
     * @private
     */
    this._session = 0;

    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
    /** @type {(() => void) | null} @private */
    this._clearUnlockHandler = null;

    this._subscribe();
    this._installUnlockHandler();
  }

  /** @returns {void} @private */
  _subscribe() {
    this._subscriptions.push(
      this._events.on(Events.STATE_CHANGED, ({ state, previous }) =>
        this._onStateChanged(state, previous)
      ),
      this._events.on(Events.DIED, (payload) => this._onDied(payload)),
      this._events.on(Events.MUSIC_CUE_CHANGED, (cue) => this._onCueChanged(cue))
    );
  }

  /**
   * Browsers refuse audible output before a user gesture; buffers can still be
   * fetched and decoded ahead of that, so only `resume()` waits for it.
   * @returns {void}
   * @private
   */
  _installUnlockHandler() {
    const unlock = () => {
      this.resume();
      this._clearUnlockHandler?.();
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    this._clearUnlockHandler = () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      this._clearUnlockHandler = null;
    };
  }

  /** @returns {void} @private */
  _ensureContext() {
    if (this._context) return;
    const Ctor = window.AudioContext ?? /** @type {any} */ (window).webkitAudioContext;
    if (!Ctor) return;

    this._context = new Ctor();
    this._master = this._context.createGain();
    this._master.gain.value = this._volume;
    this._master.connect(this._context.destination);
  }

  /**
   * Creates the music graph root if needed and resumes a suspended context.
   * The first playable gesture usually happens on the menu, before gameplay
   * starts loading stems, so creating the context here preserves the browser's
   * autoplay unlock for the later loop start.
   * @returns {void}
   */
  resume() {
    this._ensureContext();
    if (this._context?.state === 'suspended') this._context.resume();
  }

  /**
   * @returns {number} Current music volume, 0..1.
   */
  getVolume() {
    return this._volume;
  }

  /**
   * Sets the music volume, applied immediately and persisted for next launch.
   * @param {number} value New volume, 0..1 (out-of-range values are clamped).
   * @returns {void}
   */
  setVolume(value) {
    this._volume = clamp(value, 0, 1);
    if (this._master) this._master.gain.value = this._volume;
    saveNumber(VOLUME_STORAGE_KEY, this._volume);
  }

  /**
   * Fetches and decodes every layer once. Safe to call repeatedly.
   * @returns {Promise<void>}
   * @private
   */
  _load() {
    if (this._loading) return this._loading;

    this._loading = Promise.all(
      Object.entries(SOURCES).map(async ([key, url]) => {
        const response = await fetch(url);
        const bytes = await response.arrayBuffer();
        const buffer = await this._context.decodeAudioData(bytes);
        this._buffers.set(key, buffer);
      })
    ).then(() => undefined);

    return this._loading;
  }

  /**
   * @param {string} state New state name.
   * @param {string} previous Previous state name.
   * @returns {void}
   * @private
   */
  _onStateChanged(state, previous) {
    if (state === GameStates.MENU || state === GameStates.GAME_OVER) {
      // Nothing plays outside a run: cut immediately, there is no "situation"
      // left to score against.
      this._session++;
      this._stopLayers();
    } else if (state === GameStates.PLAYING && previous === GameStates.LOADING) {
      // LOADING precedes PLAYING both for a fresh run AND for every in-run
      // room change. `levelIndex` is only ever reset to 1 by `prepareNewRun`
      // (menu/game-over), so it is what tells the two apart — a room change
      // (levelIndex > 1) must leave the already-running loop untouched.
      if (this._ctx.levelIndex === 1) this._startLayers();
    }
  }

  /**
   * @returns {Promise<void>}
   * @private
   */
  async _startLayers() {
    const session = ++this._session;
    this._ensureContext();
    await this._load();
    if (session !== this._session) return; // Superseded before load finished.

    this._barDuration = this._buffers.get('snare')?.duration || this._config.barDuration;
    this._rate = 1;
    this._pulseActive = false;
    this._desiredPulse = false;
    this._desired = { scratch: false, synth: false, kick: false, bass2: false };
    this._barIndex = 0;

    // A short lead-in gives every source an identical, safely-future start
    // time — starting "now" risks scheduling a moment that has already
    // passed by the time the call actually reaches the audio thread.
    const startAt = this._context.currentTime + 0.05;

    this._layers = new Map();
    for (const key of Object.keys(SOURCES)) {
      const source = this._context.createBufferSource();
      source.buffer = this._buffers.get(key);
      source.loop = true;
      // Every layer shares the exact same loop length — measured off SNARE,
      // not each buffer's own decoded duration — so five files that are each
      // "about 3 seconds" stay sample-locked forever instead of drifting
      // apart over the course of a run.
      source.loopStart = 0;
      source.loopEnd = this._barDuration;

      const gain = this._context.createGain();
      const active = key === 'snare';
      gain.gain.setValueAtTime(active ? 1 : 0, startAt);

      source.connect(gain);
      gain.connect(this._master);
      source.start(startAt);

      this._layers.set(
        key,
        active
          ? { source, gain, active }
          : { source, gain, active, activatedAtBar: 0, pendingOff: false }
      );
    }

    // Bar 0 has begun; the earliest any toggle may land is the end of it.
    this._nextBoundaryTime = startAt + this._barDuration;
  }

  /**
   * Stops every layer. Declicks when the context is live; a plain `stop()`
   * otherwise (nothing has ever made a sound yet, so there is nothing to pop).
   * @returns {void}
   * @private
   */
  _stopLayers() {
    if (!this._layers) return;

    if (this._context) {
      const now = this._context.currentTime;
      const declick = this._config.declickTime;
      for (const layer of this._layers.values()) {
        layer.gain.gain.cancelScheduledValues(now);
        layer.gain.gain.setValueAtTime(layer.gain.gain.value, now);
        layer.gain.gain.linearRampToValueAtTime(0.0001, now + declick);
        layer.source.stop(now + declick + 0.02);
      }
    } else {
      for (const layer of this._layers.values()) {
        try {
          layer.source.stop();
        } catch {
          // Never started; nothing to do.
        }
      }
    }

    this._layers = null;
  }

  /**
   * @param {{ entity: import('../ecs/Entity.js').Entity }} payload Death event.
   * @returns {void}
   * @private
   */
  _onDied({ entity }) {
    if (!entity.has(PlayerControlled)) return;
    this._session++; // Invalidate any `_startLayers` load still in flight.
    this._stopLayers();
  }

  /**
   * Records the latest desired state. Nothing here is audible yet — every
   * field, pitch included, only takes effect at the next `_commitBoundary`.
   *
   * @param {{ scratch: boolean, synth: boolean, kick: boolean, bass2: boolean, pulse: boolean }} cue
   *   Latest snapshot from `MusicCueSystem`.
   * @returns {void}
   * @private
   */
  _onCueChanged(cue) {
    this._desired.scratch = cue.scratch;
    this._desired.synth = cue.synth;
    this._desired.kick = cue.kick;
    this._desired.bass2 = cue.bass2;
    this._desiredPulse = cue.pulse;
  }

  /**
   * Lookahead scheduler: fires every frame, but only actually schedules
   * anything once the next bar boundary is within `schedulerLookahead`
   * seconds — scheduling the automation ahead of time is what makes the
   * transition sample-accurate regardless of frame-rate jitter, since the
   * audio thread — not this per-frame call — is what executes it at the
   * exact instant. A `while` loop, not `if`, so a stall long enough to skip
   * past more than one boundary still catches up instead of leaving layers
   * stuck one bar behind.
   *
   * @param {number} _dt Delta time in seconds; unused, scheduling reads the
   *   AudioContext's own clock rather than accumulating frame deltas.
   * @returns {void}
   */
  update(_dt) {
    if (!this._layers || !this._context) return;

    const lookahead = this._config.schedulerLookahead;
    let guard = 0;
    while (this._context.currentTime >= this._nextBoundaryTime - lookahead && guard++ < 8) {
      this._commitBoundary();
    }
  }

  /**
   * Commits whatever is due at this boundary — track on/off (subject to the
   * minimum-active-loops rule) and the pitch shift — then advances to the
   * following boundary.
   * @returns {void}
   * @private
   */
  _commitBoundary() {
    const boundaryTime = this._nextBoundaryTime;
    this._barIndex++;

    for (const key of TOGGLED_LAYERS) {
      this._commitLayer(this._layers.get(key), this._desired[key], boundaryTime);
    }
    this._commitPulse(boundaryTime);

    // The bar grid adapts to the current pitch rate starting with the bar
    // that begins now; a rate change mid-bar does not retroactively shorten
    // the bar already in flight (see the `pulseSemitones` config comment).
    this._nextBoundaryTime = boundaryTime + this._barDuration / this._rate;
  }

  /**
   * Applies the minimum-active-loops rule for one track and schedules its
   * gain step if this boundary is the one where it actually changes.
   *
   * @param {{ gain: GainNode, active: boolean, activatedAtBar: number, pendingOff: boolean }} layer
   *   The layer's live state.
   * @param {boolean} desired What `MusicCueSystem` currently wants for it.
   * @param {number} boundaryTime AudioContext time of this boundary.
   * @returns {void}
   * @private
   */
  _commitLayer(layer, desired, boundaryTime) {
    if (desired && !layer.active) {
      // Fresh activation: always allowed right away.
      layer.active = true;
      layer.pendingOff = false;
      layer.activatedAtBar = this._barIndex;
      this._scheduleGain(layer.gain, 1, boundaryTime);
    } else if (desired && layer.active && layer.pendingOff) {
      // Re-triggered while waiting out the minimum window: restart it rather
      // than let the earlier, now-stale activation still count down.
      layer.pendingOff = false;
      layer.activatedAtBar = this._barIndex;
    } else if (!desired && layer.active) {
      const eligible = this._barIndex - layer.activatedAtBar >= this._config.minActiveLoops;
      if (!eligible) {
        layer.pendingOff = true;
        return;
      }
      layer.active = false;
      layer.pendingOff = false;
      this._scheduleGain(layer.gain, 0, boundaryTime);
    }
    // Else: desired matches active and nothing is pending — steady state.
  }

  /**
   * Anchor-then-ramp a gain to a new value ending exactly at `time`. Without
   * the anchor, the ramp would interpolate from whatever automation event
   * happened to run last, not from the value that has actually been holding
   * steady since the previous boundary.
   *
   * @param {GainNode} gain Layer gain node.
   * @param {0 | 1} target Target value.
   * @param {number} time AudioContext time the ramp ends at.
   * @returns {void}
   * @private
   */
  _scheduleGain(gain, target, time) {
    const declick = this._config.declickTime;
    gain.gain.cancelScheduledValues(time - declick);
    gain.gain.setValueAtTime(gain.gain.value, time - declick);
    gain.gain.linearRampToValueAtTime(target, time);
  }

  /**
   * Commits the pitch shift if it is due to change at this boundary. No
   * minimum-duration rule here — it is not a track — but same boundary gate.
   * @param {number} boundaryTime AudioContext time of this boundary.
   * @returns {void}
   * @private
   */
  _commitPulse(boundaryTime) {
    if (this._desiredPulse === this._pulseActive) return;

    this._pulseActive = this._desiredPulse;
    this._rate = this._pulseActive ? 2 ** (this._config.pulseSemitones / 12) : 1;

    const detuneCents = this._pulseActive ? this._config.pulseSemitones * 100 : 0;
    for (const layer of this._layers.values()) {
      layer.source.detune.setValueAtTime(detuneCents, boundaryTime);
    }
  }

  /**
   * Pauses or resumes playback in place, used by the pause state. Suspending
   * the `AudioContext` freezes its clock along with every scheduled
   * automation, so the whole bar grid resumes exactly where it left off with
   * no extra bookkeeping here.
   * @param {boolean} suspended Whether music should be silent.
   * @returns {void}
   */
  setSuspended(suspended) {
    if (!this._context) return;
    if (suspended) this._context.suspend();
    else this._context.resume();
  }

  /**
   * Unsubscribes and closes the audio context.
   * @returns {void}
   */
  dispose() {
    this._clearUnlockHandler?.();
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    this._stopLayers();
    this._context?.close();
    this._context = null;
    this._master = null;
    this._buffers.clear();
  }
}
